/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Select from "react-select";
import ImageInput from "../../../common/ImageInput";
import FilePreview from "../../../FilePreview/FilePreview";
import TinyEditor from "../../../Editor/TinyEditor";
import Dialog from "../../../dialog/Dialog";
import {
    createRentalSubCategory,
    getRentalCategories,
    getRentalItemById,
    getRentalSubCategoriesMaster,
    getRentalFeaturesMaster,
    createRentalFeature,
} from "../../../../services/rentalService";
import {
    resolveCategoryFormType,
    buildCategoryChangePatch,
    getTitlePlaceholder,
    parseCategoryAttributes,
    isVehicleFormType,
    RENTAL_FORM_TYPE_OPTIONS,
} from "../../../../config/rentalCategoryFormConfig";
import RentalCategoryFieldsSection from "./RentalCategoryFieldsSection";
import RentalPickupLocations from "./RentalPickupLocations";
import RentalInlineCategoryCreateDialog from "../RentalInlineCategoryCreateDialog";
import {
    normalizePickupLocationsFromRental,
    syncPrimaryLocationFields,
    validatePickupLocations,
    emptyPickupLocation,
} from "../rentalPickupLocationUtils";

const RentalGeneralInfo = ({
    setData,
    formDataRef,
    rentalId,
    validationErrors,
    submitAttempted = false,
    onValidityChange,
}) => {
    const [formData, setFormData] = useState({
        title: "",
        category_id: "",
        rental_sub_category_id: "",
        feature_ids: [],
        brand: "",
        subtitle: "",
        running_limit: "",
        unit_code: "",
        seats: "",
        fuel_type: "",
        transmission: "",
        location: "",
        latitude: "",
        longitude: "",
        pickup_locations: [emptyPickupLocation(true)],
        current_km: "",
        description: "",
        thumbnail_image: null,
        is_active: 1,
        quantity: 1,
        category_attributes: {},
    });

    const [touched, setTouched] = useState({});
    const [clientErrors, setClientErrors] = useState({});
    const [categoryActionError, setCategoryActionError] = useState("");
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [quickCreate, setQuickCreate] = useState({ open: false, type: null, value: "", error: "" });
    const queryClient = useQueryClient();
    const hydratedRentalIdRef = useRef(null);

    const markTouched = (field) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    };

    const validate = (data, formType) => {
        const errors = {};
        const req = (field, message) => {
            if (!data[field] || (typeof data[field] === "string" && data[field].trim() === "")) {
                errors[field] = message;
            }
        };

        req("title", "Title is required.");
        if (String(data.title || "").trim().length > 255) {
            errors.title = "Title must be at most 255 characters.";
        }
        req("category_id", "Category is required.");

        const pickupErrors = validatePickupLocations(data.pickup_locations);
        Object.assign(errors, pickupErrors);

        const loc = String(data.location || "").trim();
        if (!errors.location && loc.length > 500) {
            errors.location = "Location must be at most 500 characters.";
        }
        const latS = String(data.latitude ?? "").trim();
        const lngS = String(data.longitude ?? "").trim();
        if (!Object.keys(pickupErrors).length && latS && !lngS) {
            errors.longitude = "Enter longitude when latitude is set.";
        }
        if (!Object.keys(pickupErrors).length && lngS && !latS) {
            errors.latitude = "Enter latitude when longitude is set.";
        }
        if (!Object.keys(pickupErrors).length && latS) {
            const lat = Number(latS);
            if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
                errors.latitude = "Latitude must be between -90 and 90.";
            }
        }
        if (!Object.keys(pickupErrors).length && lngS) {
            const lng = Number(lngS);
            if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
                errors.longitude = "Longitude must be between -180 and 180.";
            }
        }
        if (data.current_km !== "" && data.current_km != null) {
            const km = Number(data.current_km);
            if (!Number.isFinite(km) || km < 0) {
                errors.current_km = "Odometer must be a number ≥ 0.";
            }
        }
        if (!isVehicleFormType(formType)) {
            const qty = Number(data.quantity);
            if (data.quantity === "" || data.quantity == null) {
                errors.quantity = "Quantity is required.";
            } else if (!Number.isInteger(qty) || qty < 1) {
                errors.quantity = "Quantity must be a whole number ≥ 1.";
            }
        } else {
            req("fuel_type", "Fuel type is required.");
            req("transmission", "Transmission is required.");
        }

        return errors;
    };

    useEffect(() => {
        if (formDataRef) {
            formDataRef.current = formData;
        }
        setData(formData);
    }, [formData, setData, formDataRef]);

    // Fetch Categories
    const { data: categoriesData } = useQuery({
        queryKey: ["rentalCategories"],
        queryFn: getRentalCategories,
    });

    const categories = categoriesData?.data || [];
    const categoryOptions = categories.map((cat) => ({
        value: cat.id,
        label: cat.name,
        // keep full cat object for form type resolution
        _raw: cat,
    }));

    // Derive the active category object and form type
    const selectedCategoryId = formData.category_id ? Number(formData.category_id) : null;
    const selectedCategoryObj = categories.find((c) => c.id === selectedCategoryId) || null;
    const categoryFormType = resolveCategoryFormType(selectedCategoryObj);
    const categoryFormTypeLabel =
        RENTAL_FORM_TYPE_OPTIONS.find((option) => option.value === categoryFormType)?.label || null;

    useEffect(() => {
        const errs = validate(formData, categoryFormType);
        setClientErrors(errs);
        onValidityChange?.(Object.keys(errs).length === 0);
    }, [formData, categoryFormType]);

    // Fetch Features master for the selected category type
    const { data: featuresData } = useQuery({
        queryKey: ["rentalFeaturesMaster", categoryFormType],
        queryFn: () => getRentalFeaturesMaster(categoryFormType),
        enabled: Boolean(categoryFormType),
    });
    const features = Array.isArray(featuresData?.data) ? featuresData.data : [];
    const featureOptions = features.map((f) => ({
        value: f.id,
        label: f.name,
    }));

    useEffect(() => {
        if (!categoryFormType) return;
        const validIds = new Set(features.map((f) => Number(f.id)));
        setFormData((prev) => {
            const current = prev.feature_ids || [];
            const next = current.filter((id) => validIds.has(Number(id)));
            if (next.length === current.length) return prev;
            return { ...prev, feature_ids: next };
        });
    }, [categoryFormType, features]);

    const {
        data: subCategoriesData,
        isLoading: subCatsLoading,
        isFetching: subCatsFetching,
    } = useQuery({
        queryKey: ["rentalSubCategories", selectedCategoryId],
        queryFn: () => getRentalSubCategoriesMaster(selectedCategoryId),
        enabled: Number.isFinite(selectedCategoryId) && selectedCategoryId > 0,
    });

    const subCategories = Array.isArray(subCategoriesData?.data)
        ? subCategoriesData.data
        : Array.isArray(subCategoriesData)
            ? subCategoriesData
            : [];
    const subCategoryOptions = subCategories.map((sc) => ({
        value: sc.id,
        label: sc.name,
    }));

    // Quick-create mutations
    const createSubCategoryMutation = useMutation({
        mutationFn: createRentalSubCategory,
        onSuccess: async (res) => {
            setCategoryActionError("");
            await queryClient.invalidateQueries({ queryKey: ["rentalSubCategories", selectedCategoryId] });
            const newId = res?.data?.id ?? res?.data?.data?.id;
            if (newId) {
                handleChange("rental_sub_category_id", newId);
            }
        },
        onError: (error) => {
            setCategoryActionError(error?.response?.data?.message || "Failed to create sub category.");
        },
    });

    const createFeatureMutation = useMutation({
        mutationFn: createRentalFeature,
        onSuccess: async (res) => {
            setCategoryActionError("");
            await queryClient.invalidateQueries({ queryKey: ["rentalFeaturesMaster", categoryFormType] });
            const newId = res?.data?.id ?? res?.data?.data?.id;
            if (newId) {
                setFormData((prev) => ({
                    ...prev,
                    feature_ids: Array.from(new Set([...(prev.feature_ids || []), Number(newId)])),
                }));
            }
        },
        onError: (error) => {
            setCategoryActionError(error?.response?.data?.message || "Failed to create feature.");
        },
    });

    const openQuickCreate = (type) => {
        if (type === "category") {
            setCategoryActionError("");
            setCategoryDialogOpen(true);
            return;
        }
        if (type === "sub_category" && !selectedCategoryId) {
            setCategoryActionError("Select category first, then add sub category.");
            return;
        }
        if (type === "feature" && !categoryFormType) {
            setCategoryActionError("Select a category with a category type before adding features.");
            return;
        }
        setCategoryActionError("");
        setQuickCreate({ open: true, type, value: "", error: "" });
    };

    const closeQuickCreate = () => {
        setQuickCreate({ open: false, type: null, value: "", error: "" });
    };

    const quickCreateMeta = (type) => {
        switch (type) {
            case "category":
                return { title: "Add Category", label: "Category name", placeholder: "e.g. Vehicle" };
            case "sub_category":
                return { title: "Add Sub Category", label: "Sub category name", placeholder: "e.g. SUV" };
            case "feature":
                return { title: "Add Feature", label: "Feature name", placeholder: "e.g. Bluetooth" };
            default:
                return { title: "Add", label: "Name", placeholder: "Enter name" };
        }
    };

    const submitQuickCreate = async () => {
        const raw = quickCreate?.value ?? "";
        const trimmed = String(raw).trim();
        if (!trimmed) {
            setQuickCreate((prev) => ({ ...prev, error: "Name is required." }));
            return;
        }

        try {
            if (quickCreate.type === "sub_category") {
                await createSubCategoryMutation.mutateAsync({
                    rental_category_id: Number(selectedCategoryId),
                    name: trimmed,
                    is_active: true,
                });
                closeQuickCreate();
                return;
            }
            if (quickCreate.type === "feature") {
                await createFeatureMutation.mutateAsync({
                    name: trimmed,
                    form_type: categoryFormType,
                    is_active: true,
                });
                closeQuickCreate();
                return;
            }
        } catch (e) {
            setQuickCreate((prev) => ({
                ...prev,
                error: e?.response?.data?.message || "Failed to create. Try again.",
            }));
        }
    };

    // Reset sub-category when category cleared
    useEffect(() => {
        if (!selectedCategoryId) {
            setFormData((prev) => ({ ...prev, rental_sub_category_id: "" }));
        }
    }, [selectedCategoryId]);

    // Fetch Rental Data if editing
    const { data: rentalData } = useQuery({
        queryKey: ["rentalItem", rentalId],
        queryFn: () => getRentalItemById(rentalId),
        enabled: !!rentalId,
    });

    useEffect(() => {
        hydratedRentalIdRef.current = null;
    }, [rentalId]);

    useEffect(() => {
        if (!rentalId || !rentalData?.data) return;
        if (hydratedRentalIdRef.current === rentalId) return;

        hydratedRentalIdRef.current = rentalId;
        const d = rentalData.data;
            const pickupLocations = normalizePickupLocationsFromRental(d);
            const primaryFields = syncPrimaryLocationFields(pickupLocations);
            setFormData((prev) => ({
                ...prev,
                title: d.title ?? prev.title,
                category_id: d.category_id ?? prev.category_id,
                rental_sub_category_id: d.rental_sub_category_id ?? prev.rental_sub_category_id,
                brand: d.brand ?? prev.brand,
                subtitle: d.subtitle ?? prev.subtitle,
                running_limit: d.running_limit ?? prev.running_limit,
                unit_code: d.unit_code ?? prev.unit_code,
                seats: d.seats ?? prev.seats,
                fuel_type: d.fuel_type ?? prev.fuel_type,
                transmission: d.transmission ?? prev.transmission,
                pickup_locations: pickupLocations,
                location: (primaryFields.location || d.location) ?? prev.location,
                latitude: primaryFields.latitude !== "" ? primaryFields.latitude : d.latitude ?? prev.latitude,
                longitude: primaryFields.longitude !== "" ? primaryFields.longitude : d.longitude ?? prev.longitude,
                current_km: d.current_km ?? prev.current_km,
                description: d.description ?? prev.description,
                feature_ids: Array.isArray(d.features)
                    ? d.features.map((f) => Number(f.id)).filter(Boolean)
                    : prev.feature_ids,
                thumbnail_image: d.thumbnail_image_url ?? prev.thumbnail_image,
                is_active: d.is_active ?? prev.is_active,
                quantity: d.quantity ?? prev.quantity ?? 1,
                category_attributes: parseCategoryAttributes(d.category_attributes),
            }));
    }, [rentalId, rentalData]);

    const handlePickupLocationsChange = (nextLocations) => {
        markTouched("pickup_locations");
        const primaryFields = syncPrimaryLocationFields(nextLocations);
        setFormData((prev) => ({
            ...prev,
            pickup_locations: nextLocations,
            ...primaryFields,
        }));
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleCategoryAttributeChange = (key, value) => {
        setFormData((prev) => ({
            ...prev,
            category_attributes: {
                ...(prev.category_attributes || {}),
                [key]: value,
            },
        }));
    };

    // When category changes, reset category-specific attributes and vehicle-only root fields
    const handleCategoryChange = (option) => {
        markTouched("category_id");
        const newCategoryObj = categories.find((c) => c.id === option?.value) || null;
        const newFormType = resolveCategoryFormType(newCategoryObj);
        const prevFormType = categoryFormType;

        setFormData((prev) => {
            const patch = buildCategoryChangePatch(prev, newFormType);
            return {
                ...prev,
                ...patch,
                category_id: option?.value ?? "",
                rental_sub_category_id: "",
            };
        });
        // suppress unused variable warning
        void prevFormType;
    };

    const inputClasses = (error) =>
        `w-full px-3 py-2 border ${error ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:border-primary text-sm shadow-sm transition-all`;

    const fieldError = (field) => {
        const raw = validationErrors?.[field];
        const api = Array.isArray(raw) ? raw[0] : raw;
        if (api && typeof api === "string") return api;
        if ((submitAttempted || touched[field] || touched.pickup_locations) && clientErrors[field]) return clientErrors[field];
        return "";
    };

    return (
        <div className="space-y-6">
            <RentalInlineCategoryCreateDialog
                open={categoryDialogOpen}
                onClose={() => setCategoryDialogOpen(false)}
                onCreated={(newId) => {
                    setCategoryActionError("");
                    handleChange("category_id", newId);
                    handleChange("rental_sub_category_id", "");
                }}
            />

            {/* Quick-Create Dialog (sub category / feature) */}
            <Dialog
                open={quickCreate.open}
                onClose={closeQuickCreate}
                title={quickCreateMeta(quickCreate.type).title}
                width="w-[520px]"
                closeButtonText="Cancel"
                primaryButtonText="Save"
                primaryButtonAction={submitQuickCreate}
                isLoading={
                    createSubCategoryMutation.isPending ||
                    createFeatureMutation.isPending
                }
                isPrimaryDisabled={!String(quickCreate.value || "").trim()}
            >
                <div className="p-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {quickCreateMeta(quickCreate.type).label}
                    </label>
                    <input
                        autoFocus
                        type="text"
                        value={quickCreate.value}
                        onChange={(e) =>
                            setQuickCreate((prev) => ({ ...prev, value: e.target.value, error: "" }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === "Enter") submitQuickCreate();
                        }}
                        className={inputClasses(Boolean(quickCreate.error))}
                        placeholder={quickCreateMeta(quickCreate.type).placeholder}
                    />
                    {quickCreate.error ? (
                        <div className="text-xs font-medium text-red-600 mt-2">{quickCreate.error}</div>
                    ) : null}
                </div>
            </Dialog>

            {/* ── COMMON FIELDS (always visible) ── */}
            <div className="rounded-xl border border-gray-100 bg-slate-50/40 p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Title */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Item title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => handleChange("title", e.target.value)}
                            onBlur={() => markTouched("title")}
                            className={inputClasses(Boolean(fieldError("title")))}
                            placeholder={getTitlePlaceholder(categoryFormType)}
                        />
                        {fieldError("title") ? (
                            <div className="text-xs font-medium text-red-600 mt-1.5">{fieldError("title")}</div>
                        ) : null}
                    </div>

                    {/* Brand */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Brand name <span className="text-gray-400 font-normal normal-case">(optional)</span>
                        </label>
                        <input
                            type="text"
                            value={formData.brand}
                            onChange={(e) => handleChange("brand", e.target.value)}
                            className={inputClasses(false)}
                            placeholder="e.g. Brand name"
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <div className="mb-1.5 flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700 uppercase">
                                Category <span className="text-red-500">*</span>
                            </label>
                            <button
                                type="button"
                                onClick={() => openQuickCreate("category")}
                                className="text-[11px] font-semibold text-primary hover:underline disabled:opacity-50"
                            >
                                + Add Category
                            </button>
                        </div>
                        <Select
                            options={categoryOptions}
                            value={categoryOptions.find((o) => String(o.value) === String(formData.category_id)) || null}
                            onChange={handleCategoryChange}
                            placeholder="Select Category"
                            className="text-sm"
                            menuPortalTarget={document.body}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: "0.5rem",
                                    padding: "1px",
                                    borderColor: fieldError("category_id") ? "#ef4444" : "#D1D5DB",
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                        />
                        {fieldError("category_id") ? (
                            <div className="text-xs font-medium text-red-600 mt-1.5">
                                {fieldError("category_id")}
                            </div>
                        ) : null}
                        {selectedCategoryId && !categoryFormType ? (
                            <p className="text-[11px] text-amber-600 mt-2">
                                Assign a category type to this category to manage features.
                            </p>
                        ) : null}
                    </div>

                    {!isVehicleFormType(categoryFormType) && categoryFormType && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Available quantity <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                value={formData.quantity}
                                onChange={(e) => handleChange("quantity", e.target.value)}
                                onBlur={() => markTouched("quantity")}
                                className={inputClasses(Boolean(fieldError("quantity")))}
                                placeholder="e.g. 5"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Number of identical units available to rent at the same time.
                            </p>
                            {fieldError("quantity") ? (
                                <div className="text-xs font-medium text-red-600 mt-1.5">
                                    {fieldError("quantity")}
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* Sub Category */}
                    <div>
                        <div className="mb-1.5 flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700 uppercase">
                                Sub Category{" "}
                                <span className="text-gray-400 font-normal normal-case">(optional)</span>
                            </label>
                            <button
                                type="button"
                                onClick={() => openQuickCreate("sub_category")}
                                disabled={!selectedCategoryId || createSubCategoryMutation.isPending}
                                className="text-[11px] font-semibold text-primary hover:underline disabled:opacity-50"
                            >
                                + Add Sub Category
                            </button>
                        </div>
                        <Select
                            isClearable
                            isDisabled={!selectedCategoryId || subCatsLoading || subCatsFetching}
                            options={subCategoryOptions}
                            value={
                                subCategoryOptions.find(
                                    (o) => String(o.value) === String(formData.rental_sub_category_id)
                                ) || null
                            }
                            onChange={(o) => handleChange("rental_sub_category_id", o?.value || "")}
                            placeholder={
                                !selectedCategoryId
                                    ? "Select a category first"
                                    : subCatsLoading || subCatsFetching
                                        ? "Loading…"
                                        : "Select sub category"
                            }
                            className="text-sm"
                            menuPortalTarget={document.body}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: "0.5rem",
                                    padding: "1px",
                                    borderColor: "#D1D5DB",
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                        />
                    </div>

                    {categoryFormType ? (
                        <div className="md:col-span-2">
                            <div className="mb-1.5 flex items-center justify-between">
                                <label className="block text-sm font-medium text-gray-700 uppercase">
                                    Features{" "}
                                    <span className="text-gray-400 font-normal normal-case">(optional)</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={() => openQuickCreate("feature")}
                                    disabled={createFeatureMutation.isPending}
                                    className="text-[11px] font-semibold text-primary hover:underline disabled:opacity-50"
                                >
                                    + Add Feature
                                </button>
                            </div>
                            <Select
                                isMulti
                                options={featureOptions}
                                value={featureOptions.filter((o) =>
                                    (formData.feature_ids || []).map(String).includes(String(o.value))
                                )}
                                onChange={(selected) =>
                                    handleChange(
                                        "feature_ids",
                                        Array.isArray(selected) ? selected.map((o) => o.value) : []
                                    )
                                }
                                placeholder={`Select ${categoryFormTypeLabel || "category"} features`}
                                className="text-sm"
                                menuPortalTarget={document.body}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderRadius: "0.5rem",
                                        padding: "1px",
                                        borderColor: "#D1D5DB",
                                    }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Features are scoped to {categoryFormTypeLabel || "this category type"}. You can select
                                multiple.
                            </p>
                        </div>
                    ) : null}

                    {categoryActionError ? (
                        <div className="md:col-span-2 text-xs font-medium text-red-600 -mt-2">
                            {categoryActionError}
                        </div>
                    ) : null}

                    {/* Pickup locations */}
                    <div className="md:col-span-2">
                        <RentalPickupLocations
                            locations={formData.pickup_locations}
                            onChange={handlePickupLocationsChange}
                            fieldError={fieldError}
                            markTouched={markTouched}
                        />
                    </div>

                    {/* Description */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            About / description{" "}
                            <span className="text-gray-400 font-normal normal-case">(optional)</span>
                        </label>
                        <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden">
                            <TinyEditor
                                value={formData.description}
                                onChange={(v) => handleChange("description", v)}
                                height={220}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── CATEGORY-SPECIFIC FIELDS (appear after category is selected) ── */}
            {categoryFormType && (
                <RentalCategoryFieldsSection
                    formType={categoryFormType}
                    formData={formData}
                    onChange={handleChange}
                    onCategoryAttributeChange={handleCategoryAttributeChange}
                    fieldError={fieldError}
                    inputClasses={inputClasses}
                    markTouched={markTouched}
                />
            )}

            {/* ── Hint when no category selected yet ── */}
            {!categoryFormType && formData.category_id && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                    <i className="fi fi-rr-info text-blue-500 mt-0.5"></i>
                    <p className="text-sm text-blue-700">
                        No additional fields for this category. Fill in the common details above and proceed.
                    </p>
                </div>
            )}

            {!formData.category_id && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <i className="fi fi-rr-lightbulb text-amber-500 mt-0.5"></i>
                    <p className="text-sm text-amber-700">
                        <span className="font-semibold">Tip:</span> Select a category above to see recommended
                        fields specific to that item type (vehicle specs, clothing sizes, gadget details, etc.)
                    </p>
                </div>
            )}

            {/* ── Cover thumbnail ── */}
            <div className="rounded-xl border border-gray-100 bg-slate-50/40 p-5 space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cover thumbnail <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-3">Main image shown in listing cards</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <ImageInput
                            onChange={(img) => handleChange("thumbnail_image", img)}
                            multiple={false}
                            label="Set cover photo"
                            className="bg-white h-full"
                        />
                    </div>
                    {formData.thumbnail_image && (
                        <div>
                            <FilePreview
                                file={formData.thumbnail_image instanceof File ? formData.thumbnail_image : null}
                                fileUrl={
                                    typeof formData.thumbnail_image === "string"
                                        ? formData.thumbnail_image
                                        : null
                                }
                                onDelete={() => handleChange("thumbnail_image", null)}
                                isImage={true}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RentalGeneralInfo;
